import { Button, Card, Col, Divider, Form, notification, Row, Select, Table } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { coreApi } from "../../../api/calls";
import { fetchCityBranches } from "../../../api/campaignsApi";
import { fetchManagers } from "../../../api/managerApi";

const getPartnerLabel = (partner) => {
  if (!partner) return "";

  return partner.company_name || [partner.first_name, partner.last_name].filter(Boolean).join(" ");
};

const formatCurrency = (value, currency = "CZK") => {
  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
};

const TerminatedBuildingsReport = ({ year }) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [showValuesInCZK, setShowValuesInCZK] = useState(true);
  const [countryData, setCountryData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [convertedCountryData, setConvertedCountryData] = useState([]);
  const [convertedCityData, setConvertedCityData] = useState([]);
  const [cities, setCities] = useState([]);
  const [partners, setPartners] = useState([]);
  const [managers, setManagers] = useState([]);
  const [filters, setFilters] = useState({
    city_branch_ids: null,
    partner_ids: null,
    manager_ids: null,
  });

  useEffect(() => {
    fetchManagers().then(setManagers);
    fetchCityBranches()
      .then(setCities)
      .catch(() => {
        notification.error({ message: "Error" });
      });

    coreApi
      .get("/partners/all")
      .then((res) => {
        const sortedPartners = [...(res.data || [])].sort((a, b) => getPartnerLabel(a).localeCompare(getPartnerLabel(b)));
        setPartners(sortedPartners);
      })
      .catch(() => {
        notification.error({ message: "Error" });
      });
  }, []);

  useEffect(() => {
    if (!year) return;

    setLoading(true);

    coreApi
      .get("/reports/country/terminated-buildings", {
        params: {
          year: year.format("YYYY-MM-DD"),
          city_branch_ids: filters.city_branch_ids || null,
          partnerIds: filters.partner_ids || null,
          userIds: filters.manager_ids || null,
        },
      })
      .then((res) => {
        setCountryData(res.data.terminated_buildings_country || []);
        setCityData(res.data.terminated_buildings_city || []);
        setConvertedCountryData(
          res.data.aggregate_total
            ? [res.data.aggregate_total, ...(res.data.converted_terminated_buildings_country || [])]
            : res.data.converted_terminated_buildings_country || []
        );
        setConvertedCityData(res.data.converted_terminated_buildings_city || []);
      })
      .catch((err) => notification.error({ message: err?.response?.data?.message || "Error" }))
      .finally(() => {
        setLoading(false);
      });
  }, [filters, year]);

  const getMonthColumns = (totalPrefix) => {
    return [
      ...Array.from({ length: 12 }, (_, i) => ({
        title: moment().month(i).format("MMMM"),
        children: [
          {
            title: t("reports.country.terminatedBuildingsCount"),
            key: `count_${i + 1}`,
            dataIndex: `count_${i + 1}`,
            width: 100,
            sorter: (a, b) => Number(a[`count_${i + 1}`] || 0) - Number(b[`count_${i + 1}`] || 0),
            render: (value) => Number(value || 0),
          },
          {
            title: t("reports.country.terminatedBuildingsSum"),
            key: `sum_${i + 1}`,
            dataIndex: `sum_${i + 1}`,
            width: 130,
            sorter: (a, b) => Number(a[`sum_${i + 1}`] || 0) - Number(b[`sum_${i + 1}`] || 0),
            render: (value, row) => formatCurrency(value, row?.currency_iso_4217),
          },
        ],
      })),
      {
        title: t("common.total"),
        fixed: "right",
        children: [
          {
            title: t("reports.country.terminatedBuildingsCount"),
            key: `${totalPrefix}_total_count`,
            dataIndex: `${totalPrefix}_total_count`,
            width: 100,
            sorter: (a, b) => Number(a[`${totalPrefix}_total_count`] || 0) - Number(b[`${totalPrefix}_total_count`] || 0),
            render: (value) => Number(value || 0),
          },
          {
            title: t("reports.country.terminatedBuildingsSum"),
            key: `${totalPrefix}_total_sum`,
            dataIndex: `${totalPrefix}_total_sum`,
            width: 130,
            sorter: (a, b) => Number(a[`${totalPrefix}_total_sum`] || 0) - Number(b[`${totalPrefix}_total_sum`] || 0),
            render: (value, row) => formatCurrency(value, row?.currency_iso_4217),
          },
        ],
      },
    ];
  };

  const countryColumns = [
    {
      title: t("common.country"),
      key: "country",
      width: 180,
      fixed: "left",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (row) => {
        if (!row.country_branch_id) return row.name;

        return <Link to={`/countries/${row.country_branch_id}`}>{row.name}</Link>;
      },
    },
    ...getMonthColumns("country"),
  ];

  const cityColumns = [
    {
      title: t("common.cityBranch"),
      key: "city",
      width: 180,
      fixed: "left",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (row) => {
        return <Link to={`/countries/${row.country_branch_id}/cities/${row.city_branch_id}`}>{row.name}</Link>;
      },
    },
    ...getMonthColumns("city"),
  ];

  const countryRows = showValuesInCZK ? convertedCountryData : countryData;
  const cityRows = showValuesInCZK ? convertedCityData : cityData;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Form layout="inline">
          <Form.Item label={t("common.cityBranch")}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 280 }}
              placeholder={t("expansion.selectCities")}
              value={filters.city_branch_ids || []}
              onChange={(value) => setFilters((prev) => ({ ...prev, city_branch_ids: value.length ? value : null }))}
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              optionLabelProp="label"
              showSearch
            >
              {cities.map((city) => (
                <Select.Option key={city.id} value={city.id} label={city.name}>
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t("cleanings.partner")}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 280 }}
              placeholder={t("cleanings.partner")}
              value={filters.partner_ids || []}
              onChange={(value) => setFilters((prev) => ({ ...prev, partner_ids: value.length ? value : null }))}
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              optionLabelProp="label"
              showSearch
            >
              {partners.map((partner) => (
                <Select.Option key={partner.id} value={partner.id} label={getPartnerLabel(partner)}>
                  {getPartnerLabel(partner)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t("invoices.manager")}>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 280 }}
              placeholder={t("managers.selectManager")}
              value={filters.manager_ids || []}
              onChange={(value) => setFilters((prev) => ({ ...prev, manager_ids: value.length ? value : null }))}
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              optionLabelProp="label"
              showSearch
            >
              {managers.map((manager) => (
                <Select.Option
                  key={manager.user?.id}
                  value={manager.user?.id}
                  label={`${manager.first_name} ${manager.last_name}`}
                >
                  {manager.user?.deleted_at === null ? (
                    `${manager.first_name} ${manager.last_name}`
                  ) : (
                    <s>{`${manager.first_name} ${manager.last_name}`}</s>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button onClick={() => setShowValuesInCZK(!showValuesInCZK)}>
              {showValuesInCZK ? t("reports.country.showOrigVals") : t("reports.country.showCZKVals")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
      <Divider />
      <Col span={24}>
        <Card loading={isLoading} size="small" bodyStyle={{ padding: "10px" }}>
          <Table
            rowKey={(row, index) => `${row.name}-${index}`}
            loading={isLoading}
            size="small"
            dataSource={countryRows}
            columns={countryColumns}
            pagination={false}
            scroll={{ x: 2400 }}
          />
        </Card>
      </Col>
      <Divider />
      <Col span={24}>
        <Card loading={isLoading} size="small" bodyStyle={{ padding: "10px" }}>
          <Table
            rowKey={(row) => `${row.city_branch_id}`}
            loading={isLoading}
            size="small"
            dataSource={cityRows}
            columns={cityColumns}
            pagination={false}
            scroll={{ y: "calc(100vh - 320px)", x: 2400 }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default TerminatedBuildingsReport;
